import { reactive } from 'vue'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export const pwaInstall = reactive({ available: false })

let deferredPrompt: BeforeInstallPromptEvent | null = null

export function initializePwaInstall(): () => void {
  if (isInstalledPwa()) {
    pwaInstall.available = false
    deferredPrompt = null
    return () => {}
  }

  const onPrompt = (event: Event) => {
    event.preventDefault()
    if (isInstalledPwa()) {
      deferredPrompt = null
      pwaInstall.available = false
      return
    }
    deferredPrompt = event as BeforeInstallPromptEvent
    pwaInstall.available = true
  }
  const hide = () => {
    deferredPrompt = null
    pwaInstall.available = false
  }
  const onDisplayMode = () => {
    if (isInstalledPwa()) hide()
  }

  window.addEventListener('beforeinstallprompt', onPrompt)
  window.addEventListener('appinstalled', hide)
  const media = typeof window.matchMedia === 'function' ? window.matchMedia('(display-mode: standalone)') : undefined
  media?.addEventListener('change', onDisplayMode)

  return () => {
    window.removeEventListener('beforeinstallprompt', onPrompt)
    window.removeEventListener('appinstalled', hide)
    media?.removeEventListener('change', onDisplayMode)
    hide()
  }
}

export async function promptPwaInstall(): Promise<void> {
  const prompt = deferredPrompt
  if (!prompt) return
  deferredPrompt = null
  try {
    await prompt.prompt()
    await prompt.userChoice
  } catch {
    // The browser only allows one prompt per event.
  } finally {
    pwaInstall.available = false
  }
}

export function isInstalledPwa(): boolean {
  if (typeof window.matchMedia === 'function') {
    if (window.matchMedia('(display-mode: standalone)').matches) return true
    if (window.matchMedia('(display-mode: window-controls-overlay)').matches) return true
    if (window.matchMedia('(display-mode: fullscreen)').matches) return true
  }
  return (window.navigator as Navigator & { standalone?: boolean }).standalone === true
}
